/* eslint-disable max-lines */
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import LinearProgress from '@material-ui/core/LinearProgress';
import Typography from '@material-ui/core/Typography';
import FormValidation from '@react-form-fields/material-ui/components/FormValidation';
import FieldSelect from '@react-form-fields/material-ui/components/Select';
import FieldText from '@react-form-fields/material-ui/components/Text';
import { theme } from 'assets/theme';
import { FormComponent, IStateForm } from 'components/Abstract/Form';
import ErrorMessage from 'components/Shared/ErrorMessage';
import Snackbar from 'components/Shared/Snackbar';
import { WithStyles } from 'decorators/withStyles';
import { enCriticality, enFrequency, IDocument, NA } from 'interfaces/document';
import { IEntity } from 'interfaces/entity';
import React from 'react';
import { combineLatest } from 'rxjs';
import RxOp from 'rxjs-operators';
import documentService from 'services/document';
import entityService from 'services/entity';
import selectedVentureService from 'services/profile';
import ventureService from 'services/venture';

/* eslint-disable max-lines */
const optionsCriticality = Object.values(enCriticality).map(criticality => ({
  value: criticality, label: criticality
}));

const optionsFrequency = Object.values(enFrequency).map(frequency => ({
  value: frequency, label: frequency
}));

interface ISelectOption {
  label: string,
  value: string | number,
  ids?: number[]
}

interface IState extends IStateForm<IDocument> {
  loading: boolean;
  error?: null;
  selectorOpened: boolean;
  entitiesGraph?: IEntity[];
  entityOptions?: ISelectOption[];
  tabOptions?: ISelectOption[];
  subTabOptions?: ISelectOption[];
  disableFrequency?: boolean;
  ventures: ISelectOption[];
  venturesOptions: string[];
}

interface IProps {
  opened: boolean;
  document?: IDocument;
  onComplete: (document: IDocument) => void;
  onCancel: () => void;
  classes?: any;
}

const sortByValue = (a: ISelectOption, b: ISelectOption) => {
  if (a.value < b.value) return -1;
  if (a.value > b.value) return 1;
  return 0;
};

@WithStyles({
  dialog: {
    width: 1700,
    height: 700,
    maxWidth: '100vw',
  },
  root: {
    maxWidth: '100vw',
  },
  Fragment: {
    marginBottom: 20,
    maxWidth: '100vw',
  },
  img: {
    maxWidth: '100%',
    margin: 'auto',
    display: 'block',
    padding: 5,
    boxShadow: theme.shadows['5']
  },
  cardActions: {
    justifyContent: 'flex-end'
  },
  content: {
    width: 1700,
    height: 700,
    maxWidth: '100%'
  },
  textField: {
    margin: 8
  },
  heading: {
    marginTop: 30,
    marginBottom: 20
  },
  ImageSelector: {
    marginTop: 30
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 200,
    maxWidth: '100vw',
  },
  Card: {
    marginTop: 30,
    maxWidth: '100vw',
  },
  Paper: {
    width: 600,
    height: 500,
    maxWidth: '100vw',
  },
  dialogActions: {
    padding: theme.spacing(2)
  },
})
export default class DocumentFormDialog extends FormComponent<IProps, IState> {
  constructor(props: {}) {
    super(props);
    this.state = {
      ...this.state,
      loading: true,
      selectorOpened: false,
      entitiesGraph: null,
      disableFrequency: false,
      entityOptions: [] as ISelectOption[],
      tabOptions: [] as ISelectOption[],
      subTabOptions: [] as ISelectOption[],
      ventures: [] as ISelectOption[],
      venturesOptions: [] as string[],
    };
  }

  get isEdit(): boolean {
    return !!this.state.model.id;
  }

  handleEnter = () => {
    const { document } = this.props;
    const model: IState['model'] = document || {};
    selectedVentureService.getSelectedVenture().pipe(
      RxOp.bindComponent(this),
      RxOp.logError(),
    ).subscribe(selection => {
      model.entityName = model.entity ? model.entity.name : '';
      model.tabName = model.tab ? model.entityName + '|' + model.tab.name : '';
      model.subTabName = model.subTab ? model.tabName + '|' + model.subTab.name : '';
      this.setState({ model, disableFrequency: model.frequency === NA });
      this.loadData();
    });
  };

  handleExit = () => {
    this.setState({ disableFrequency: false });
    this.resetForm();
  };

  componentDidMount() {
    this.loadData();
  }

  loadData = () => {
    this.setState({ loading: false, error: null });
    combineLatest([
      ventureService.list({ page: 0, pageSize: 100 }),
    ]).pipe(
      RxOp.logError(),
      RxOp.bindComponent(this),
      RxOp.filter(([venturesResponse]) => Boolean(venturesResponse))
    ).subscribe(([venturesResponse]) => {
      const ventures = venturesResponse.results.map(v => ({ label: `${v.name}`, value: v.id }));

      this.setState({
        ventures,
      });
    });

    entityService.list({ page: 0, pageSize: Number.MAX_SAFE_INTEGER }).subscribe(graph => {
      if (!graph || !graph.results) return;
      const entityOptions: ISelectOption[] = this.getModuleOptions(graph.results);
      const tabOptions = this.isEdit && this.state.model.entityName ? this.getTabOptions(this.state.model.entityName) : [];
      const subTabOptions = this.isEdit && this.state.model.tabName ? this.getSubTabOptions(this.state.model.tabName) : [];
      this.setState({
        entitiesGraph: graph.results,
        entityOptions,
        tabOptions,
        subTabOptions,
      });
    }, error => console.log(error));
  };

  getModuleOptions = (graph: IEntity[]) => {
    const modules: ISelectOption[] = [];
    graph.filter((entity: IEntity) => !entity.parentId)
      .forEach((entity: any) => {
        let module = modules.find(m => m.value === entity.name);
        if (!module) {
          modules.push({ label: entity.name, value: entity.name, ids: [] });
          module = modules.find(m => m.value === entity.name);
        }
        module.ids.push(entity.id);
      });
    modules.sort(sortByValue);
    return modules;
  };

  getTabOptions = (value: string | number) => {
    const { entitiesGraph: graph } = this.state;
    const tabOptions: ISelectOption[] = [];
    if (value) {
      const modules = graph.filter(entity => entity.level === 0 && (entity.name === value || entity.id === value));
      if (modules.length) {
        graph
          .filter(tab => tab.level === 1 && modules.map(e => e.id).includes(tab.parentId))
          .forEach((tab: any) => {
            const moduleTab = `${modules[0].name}|${tab.name}`;
            let tabOption = tabOptions.find(t => t.value === moduleTab);
            if (!tabOption) {
              tabOptions.push({ label: tab.name, value: moduleTab, ids: [] });
              tabOption = tabOptions.find(t => t.value === moduleTab);
            }
            tabOption.ids.push(tab.id);
          });
      }
    }
    tabOptions.sort(sortByValue);
    return tabOptions;
  };

  getSubTabOptions = (value: string | number) => {
    const { entitiesGraph: graph } = this.state;
    const subTabOptions: ISelectOption[] = [];
    if (value) {
      if (typeof value === 'string') {
        const moduleTab = value.split('|');
        if (moduleTab.length === 2) {
          const moduleTabOption = this.getTabOptions(moduleTab[0]).find(tab => tab.value === value);
          if (moduleTabOption) {
            graph.filter(subTab => subTab.level === 2 && moduleTabOption.ids.includes(subTab.parentId)).forEach((subTab: any) => {
              if (!subTabOptions.map(t => t.value).includes(`${value}|${subTab.name}`)) {
                subTabOptions.push({
                  label: `${subTab.name}`,
                  value: `${value}|${subTab.name}`
                });
              }
            });
          }
        }
      }
    }
    subTabOptions.sort(sortByValue);
    return subTabOptions;
  };

  onSubmit = (isValid: boolean) => {
    const { model } = this.state;

    const { onComplete } = this.props;

    if (!isValid) return;
    if (model.entityName === '-1') {
      delete model.entityName;
    }
    if (model.tabName === '-1') {
      delete model.tabName;
    }
    if (model.subTabName === '-1') {
      delete model.subTabName;
    }

    this.setState({ loading: true });
    delete model.entity;
    delete model.venture;
    delete model.tab;
    delete model.subTab;

    if(Array.isArray(model.ventureId)){
      (model as any).ventureId = model.ventureId.filter(id => id);
    }

    documentService.saveMultipleDocs(model as IDocument).pipe(
      RxOp.logError(),
      RxOp.bindComponent(this)
    ).subscribe(document => {
      Snackbar.show(`${document.name} foi salvo com sucesso!`);
      this.setState({ loading: false });

      onComplete(document);
    }, err => {
      Snackbar.error(err.message === 'email-unavailable' ? 'email já utilizado' : err);
      this.setState({ loading: false });
    });
  };

  updateTabOptions = (value: any) => {
    this.setState({
      tabOptions: this.getTabOptions(value),
      subTabOptions: [],
    });
  };

  updateSubTabOptions = (value: any) => {
    this.setState({ subTabOptions: this.getSubTabOptions(value) });
  };

  handleChangeEntity = (value: string) => {
    this.updateTabOptions(value);
    this.updateModel((model, v) => {
      model.entityName = v;
      model.tabName = model.subTabName = '-1';
    })(value);
  };

  handleChangeTab = (value: string) => {
    this.updateSubTabOptions(value);
    this.updateModel((model, v) => {
      model.tabName = v;
      model.subTabName = '-1';
    })(value);
  };

  handleFrequencyNA = () => {
    const disableFrequency = !this.state.disableFrequency;
    const frequency = disableFrequency ? NA : '';
    this.setState({ disableFrequency });
    this.updateModel((model, v) => model.frequency = v)(frequency);
  };

  render() {
    const {
      ventures,
      model, loading, error, disableFrequency, entitiesGraph, entityOptions, tabOptions, subTabOptions
    } = this.state;
    const { opened, classes, onCancel } = this.props;
    if (!entitiesGraph) return '';

    return (
      <Dialog
        open={opened}
        disableBackdropClick
        disableEscapeKeyDown
        onEnter={this.handleEnter}
        onExited={this.handleExit}
      >
        {loading && <LinearProgress color="secondary"/>}

        <FormValidation onSubmit={this.onSubmit} ref={this.formValidationRef}>
          <DialogTitle>{this.isEdit ? 'Editar' : 'Novo'} Documento</DialogTitle>
          <DialogContent className={classes.content}>
            {error && (<ErrorMessage error={error} tryAgain={this.loadData}/>)}

            {!error &&
            <Typography component="div" dir={theme.direction}>
              <div className={classes.field}>
                <FieldSelect
                  label={'Empreendimento' + (!this.isEdit ? 's' : '')}
                  multiple={!this.isEdit}
                  disabled={loading}
                  validation={'required'}
                  value={this.isEdit || Array.isArray(model.ventureId) ? model.ventureId : [model.ventureId]}
                  variant="outlined"
                  onChange={this.updateModel((model, v) => (model.ventureId = v))}
                >
                  {ventures.map((venture) => (
                    <MenuItem key={venture.value} value={venture.value}>
                      {venture.label}
                    </MenuItem>
                  ))}
                </FieldSelect>
                <FieldText
                  label="Nome"
                  validation="required|string|max:1000"
                  disabled={loading}
                  value={model.name}
                  onChange={this.updateModel((model, v) => model.name = v)}
                />
                <FieldSelect
                  label="Periodicidade"
                  disabled={loading || disableFrequency}
                  value={model.frequency}
                  options={disableFrequency ? [{ value: NA, label: NA }] : optionsFrequency}
                  validation="required"
                  onChange={this.updateModel((model, v) => model.frequency = v)}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={disableFrequency}
                      onChange={this.handleFrequencyNA}
                      value="disableFrequency"
                      color="primary"
                    />
                  }
                  label="N/A - Não Aplicável"
                />
                <FieldSelect
                  label="Criticidade"
                  disabled={loading}
                  value={model.criticality}
                  options={optionsCriticality}
                  validation="required"
                  onChange={this.updateModel((model, v) => model.criticality = v)}
                />
                <FieldText
                  label="Dúvida"
                  disabled={loading}
                  value={model.doubt}
                  multiline
                  validation="required|string|max:1000"
                  onChange={this.updateModel((model, v) => model.doubt = v)}
                />
                <FieldText
                  label="Título URL"
                  disabled={loading}
                  value={model.titleHyperLink}
                  validation="string|max:100"
                  onChange={this.updateModel((model, v) => model.titleHyperLink = v)}
                />
                <FieldText
                  label="URL"
                  disabled={loading}
                  value={model.hyperLink}
                  validation="string|max:1000"
                  onChange={this.updateModel((model, v) => model.hyperLink = v)}
                />
                <FieldSelect
                  label="Selecione o Módulo"
                  disabled={loading}
                  value={model.entityName}
                  options={entityOptions}
                  validation="required|string|min:1"
                  onChange={this.handleChangeEntity}

                />
                <FieldSelect
                  label="Selecione a Aba"
                  disabled={loading}
                  value={model.tabName}
                  options={tabOptions}
                  validation="required|string|min:1"
                  onChange={this.handleChangeTab}
                  emptyOption={'Selecione um módulo primeiro'}
                />
                <FieldSelect
                  label="Selecione a Subaba"
                  disabled={loading}
                  value={model.subTabName}
                  validation={subTabOptions.length > 0 ? 'required|string|min:1' : null}
                  options={subTabOptions}
                  onChange={this.updateModel((model, v) => model.subTabName = v)}
                  emptyOption={'Nenhuma Subaba para essa aba selecionada'}
                />
              </div>
            </Typography>
            }
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <Button onClick={onCancel}>
              Cancelar
            </Button>
            <Button color="primary" variant={'contained'} type="submit" disabled={loading || !!error}>
              Salvar
            </Button>
          </DialogActions>
        </FormValidation>
      </Dialog>
    );
  }
}